import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import { Plus, Filter, Bell, AlertTriangle, ChevronDown } from 'lucide-react';
const Actions: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'sales' | 'inventory' | 'reporting' | 'account'>('sales');
  const columns = [{
    key: 'name',
    label: 'Action Name',
    sortable: true
  }, {
    key: 'category',
    label: 'Category',
    sortable: true
  }, {
    key: 'description',
    label: 'Description',
    sortable: true
  }, {
    key: 'occurrences',
    label: 'Occurrences',
    sortable: true
  }, {
    key: 'lastTriggered',
    label: 'Last Triggered',
    sortable: true
  }];
  const salesActions = [{
    id: 1,
    name: 'Sales Order Created',
    category: 'Sales & Order Management',
    description: 'A user initiates a new sales order',
    occurrences: 156,
    lastTriggered: '5 minutes ago'
  }, {
    id: 2,
    name: 'Sales Order Updated',
    category: 'Sales & Order Management',
    description: 'An existing sales order is modified',
    occurrences: 89,
    lastTriggered: '15 minutes ago'
  }, {
    id: 3,
    name: 'Sales Order Fulfilled',
    category: 'Sales & Order Management',
    description: 'An order is marked as complete and ready for delivery',
    occurrences: 42,
    lastTriggered: '1 hour ago'
  }, {
    id: 4,
    name: 'Invoice Created',
    category: 'Sales & Order Management',
    description: 'An invoice is generated from a sales order',
    occurrences: 37,
    lastTriggered: '2 hours ago'
  }, {
    id: 5,
    name: 'Invoice Sent',
    category: 'Sales & Order Management',
    description: 'An invoice is emailed to a customer',
    occurrences: 34,
    lastTriggered: '3 hours ago'
  }, {
    id: 6,
    name: 'Payment Received',
    category: 'Sales & Order Management',
    description: 'A payment is logged against an invoice',
    occurrences: 28,
    lastTriggered: '4 hours ago'
  }, {
    id: 7,
    name: 'Purchase Order Created',
    category: 'Sales & Order Management',
    description: 'A user creates a new purchase order for incoming goods',
    occurrences: 15,
    lastTriggered: '6 hours ago'
  }, {
    id: 8,
    name: 'Purchase Order Received',
    category: 'Sales & Order Management',
    description: 'Incoming goods from a PO are accepted into inventory',
    occurrences: 12,
    lastTriggered: '8 hours ago'
  }, {
    id: 9,
    name: 'Transfer Manifest Created',
    category: 'Sales & Order Management',
    description: 'A manifest for moving cannabis products is generated and pushed to a state compliance system',
    occurrences: 8,
    lastTriggered: '12 hours ago'
  }, {
    id: 10,
    name: 'E-commerce Menu Used',
    category: 'Sales & Order Management',
    description: 'A user interacts with the wholesale e-commerce menu',
    occurrences: 67,
    lastTriggered: '30 minutes ago'
  }];
  const inventoryActions = [{
    id: 11,
    name: 'Inventory Adjusted',
    category: 'Inventory & Production',
    description: 'A user manually changes the quantity of an inventory item',
    occurrences: 48,
    lastTriggered: '1 hour ago'
  }, {
    id: 12,
    name: 'New Package Created',
    category: 'Inventory & Production',
    description: 'A user creates a new package within the system, often by splitting an existing one',
    occurrences: 32,
    lastTriggered: '2 hours ago'
  }, {
    id: 13,
    name: 'Assembly Created',
    category: 'Inventory & Production',
    description: 'A user starts a manufacturing process, combining cannabis and non-cannabis goods to create a new product',
    occurrences: 18,
    lastTriggered: '4 hours ago'
  }, {
    id: 14,
    name: 'Waste Logged',
    category: 'Inventory & Production',
    description: 'A user records cannabis waste, which is critical for compliance',
    occurrences: 9,
    lastTriggered: '6 hours ago'
  }, {
    id: 15,
    name: 'Label Template Created',
    category: 'Inventory & Production',
    description: 'A user designs a new dynamic label template',
    occurrences: 5,
    lastTriggered: '1 day ago'
  }, {
    id: 16,
    name: 'Label Printed',
    category: 'Inventory & Production',
    description: 'A user prints a compliance label for a package',
    occurrences: 73,
    lastTriggered: '30 minutes ago'
  }, {
    id: 17,
    name: 'Inventory Audited',
    category: 'Inventory & Production',
    description: 'A user performs a cycle count or inventory audit',
    occurrences: 7,
    lastTriggered: '12 hours ago'
  }];
  const reportingActions = [{
    id: 18,
    name: 'Report Exported',
    category: 'Reporting & Analytics',
    description: 'A user exports a "base" or "OG" report, often to a CSV file for manual analysis',
    occurrences: 86,
    lastTriggered: '15 minutes ago'
  }, {
    id: 19,
    name: 'Report Viewed',
    category: 'Reporting & Analytics',
    description: 'A user views a report within the Distru UI',
    occurrences: 124,
    lastTriggered: '5 minutes ago'
  }, {
    id: 20,
    name: 'Brand Filter Used',
    category: 'Reporting & Analytics',
    description: 'A user filters a report by a specific brand name',
    occurrences: 45,
    lastTriggered: '1 hour ago'
  }, {
    id: 21,
    name: 'Advanced Analytics Dashboard Viewed',
    category: 'Reporting & Analytics',
    description: 'A user with the analytics add-on views a dashboard',
    occurrences: 28,
    lastTriggered: '2 hours ago'
  }];
  const accountActions = [{
    id: 22,
    name: 'New User Invited',
    category: 'Account & Administration',
    description: 'An administrator adds a new user to their Distru account',
    occurrences: 12,
    lastTriggered: '5 hours ago'
  }, {
    id: 23,
    name: 'User Role Changed',
    category: 'Account & Administration',
    description: "A user's permissions are modified",
    occurrences: 8,
    lastTriggered: '8 hours ago'
  }, {
    id: 24,
    name: 'Integration Synced',
    category: 'Account & Administration',
    description: 'A sync is performed with an external tool like QuickBooks',
    occurrences: 34,
    lastTriggered: '2 hours ago'
  }, {
    id: 25,
    name: 'API Key Generated',
    category: 'Account & Administration',
    description: 'A user creates an API key for custom integrations',
    occurrences: 3,
    lastTriggered: '2 days ago'
  }];
  const getActiveData = () => {
    switch (activeTab) {
      case 'sales':
        return salesActions;
      case 'inventory':
        return inventoryActions;
      case 'reporting':
        return reportingActions;
      case 'account':
        return accountActions;
      default:
        return salesActions;
    }
  };
  const handleActionClick = (action: any) => {
    navigate(`/actions/${action.id}`);
  };
  return <div className="space-y-6" data-id="element-683">
      <div className="flex justify-between items-center" data-id="element-684">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-685">User Actions</h1>
        <button className="bg-[#0070d2] hover:bg-[#0057a3] text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-686">
          <Plus size={18} data-id="element-687" />
          <span data-id="element-688">New Action</span>
        </button>
      </div>
      <div className="grid grid-cols-4 gap-4" data-id="element-689">
        <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-690">
          <div className="flex items-center gap-3" data-id="element-691">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600" data-id="element-692">
              <Bell size={20} data-id="element-693" />
            </div>
            <div data-id="element-694">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-695">
                Total Actions
              </h3>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-696">25</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-697">
          <div className="flex items-center gap-3" data-id="element-698">
            <div className="p-2 rounded-full bg-green-100 text-green-600" data-id="element-699">
              <Bell size={20} data-id="element-700" />
            </div>
            <div data-id="element-701">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-702">
                Today's Events
              </h3>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-703">842</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-704">
          <div className="flex items-center gap-3" data-id="element-705">
            <div className="p-2 rounded-full bg-purple-100 text-purple-600" data-id="element-706">
              <Bell size={20} data-id="element-707" />
            </div>
            <div data-id="element-708">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-709">
                Active Users
              </h3>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-710">47</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-711">
          <div className="flex items-center gap-3" data-id="element-712">
            <div className="p-2 rounded-full bg-red-100 text-red-600" data-id="element-713">
              <AlertTriangle size={20} data-id="element-714" />
            </div>
            <div data-id="element-715">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-716">
                Active Alerts
              </h3>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-717">8</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-718">
        <div className="border-b border-gray-200" data-id="element-719">
          <nav className="flex -mb-px" data-id="element-720">
            <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'sales' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('sales')} data-id="element-721">
              Sales & Orders
            </button>
            <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'inventory' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('inventory')} data-id="element-722">
              Inventory & Production
            </button>
            <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'reporting' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('reporting')} data-id="element-723">
              Reporting & Analytics
            </button>
            <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'account' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('account')} data-id="element-724">
              Account & Admin
            </button>
          </nav>
        </div>
        <DataTable columns={columns} data={getActiveData()} onRowClick={handleActionClick} data-id="element-725" />
      </div>
      
    </div>;
};
export default Actions;